import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DataService } from '@services/ops-profiledata.service';
import { GlobalErrorService } from '@services/global-error.service';
import {
  ToastTitle,
  VendorProfile,
  vendorProfileInfo,
  HeaderText,
} from '@constants/global.enum';
import { vendorInfo } from '@interfaces/vendor-profile.model';
import {
  getFormControlError,
  restrictInvalidKeyPress,
} from '@utils/validators';
import { ToasterService } from '@services/toaster.service';
import { yearDateFormat } from '@constants/dateFormat';
import { formatSingleDate } from '@utils/utils';
import { validateNumberInput } from '@utils/validators';
import { LENGTH } from '@shared/config/allowedKeys.config';

@Component({
  selector: 'app-vendor-info',
  templateUrl: './vendor-info.component.html',
  styleUrls: ['./vendor-info.component.scss'],
})
export class VendorInfoComponent implements OnInit {
  @Input() vendorId: string | null = null;
  vendorProfileInfo = vendorProfileInfo;
  headerText = HeaderText;
  about: Partial<vendorInfo> = {};
  vendorContact: Partial<vendorInfo>[] = [];
  maxLengthTen = LENGTH.MAX_LENGTH_TEN;
  selectedContactIndex: number | null = null;
  selectedContactId: string = '';

  aboutForm!: FormGroup;
  contactForm!: FormGroup;
  opened = false;
  openedCi = false;
  isSubmitted: boolean = false;

  validate = validateNumberInput;

  aboutDetails: { label: string; key: keyof vendorInfo }[] = [
    {
      label: vendorProfileInfo.labelVendorPSID,
      key: vendorProfileInfo.keyVendorId as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelFullAddress,
      key: vendorProfileInfo.keyFullAddress as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelEmailID,
      key: vendorProfileInfo.keyEmailVendor as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelDateOfOnboarding,
      key: vendorProfileInfo.keyDateOfOnboarding as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelMSAExpiryDate,
      key: vendorProfileInfo.keyMsaexpiryDate as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelC2H,
      key: vendorProfileInfo.keyC2H as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelReleaseTimeline,
      key: vendorProfileInfo.keyReleaseTimeline as keyof vendorInfo,
    },
  ];

  contactDetails: { label: string; key: keyof vendorInfo }[] = [
    {
      label: vendorProfileInfo.labelPointOfContact,
      key: vendorProfileInfo.keyPointOfContact as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelDesignation,
      key: vendorProfileInfo.keyDesignation as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelEmail,
      key: vendorProfileInfo.keyEmail as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelMobileNumber,
      key: vendorProfileInfo.keyMobileNumber as keyof vendorInfo,
    },
    {
      label: vendorProfileInfo.labelLocation,
      key: vendorProfileInfo.keyLocation as keyof vendorInfo,
    },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private dataService: DataService,
    private errorHandler: GlobalErrorService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.route.params.subscribe((p) => {
      if (p['vendorId']) this.vendorId = p['vendorId'];
      if (this.vendorId) this.loadVendor();
    });
  }

  buildForms() {
    this.aboutForm = this.fb.group({
      vendorId: [{ value: '', disabled: true }],
      fullAddress: ['', [Validators.required]],
      emailVendor: ['', [Validators.required, Validators.email]],
      dateOfOnboarding: ['', [Validators.required]],
      msaexpiryDate: ['', [Validators.required]],
      c2h: [''],
      releaseTimeline: [''],
      });

    this.contactForm = this.fb.group({
      pointOfContact: ['', [Validators.required]],
      designation: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      contact: ['', [Validators.required]],
      location: ['', [Validators.required]],
    });
  }

  loadVendor() {
    this.dataService.getVendorData(this.vendorId).subscribe({
      next: (res) => {
        const rows = res?.data || [];
        const base = rows[0];
        if (base) {
          this.about = {
            vendorId: base.vendorId ?? '',
            fullAddress: base.fullAddress ?? '',
            emailVendor: base.emailVendor ?? '',
            dateOfOnboarding: base.dateOfOnboarding ?? '',
            msaexpiryDate: base.msaexpiryDate ?? '',
            c2h: base.c2h ?? '',
            releaseTimeline: base.releaseTimeline ?? '',
          };
          this.vendorContact = rows.slice(1).map((c) => ({
            pointOfContact: c.pointOfContact ?? '',
            designation: c.designation ?? '',
            email: c.email ?? '',
            contact: c.contact ?? '',
            location: c.location ?? '',
            vendorDetailsId: c.vendorDetailsId ?? '',
          }));
        }
      },
      error: (err) => {
        this.errorHandler.logComponentError(err);
      },
    });
  }

  openAboutDialog() {
    this.aboutForm.patchValue(this.about);
  }

  saveAboutDialog() {
    this.isSubmitted = true;
    if (this.aboutForm.invalid) {
      this.aboutForm.markAllAsTouched();
      return;
    }
    const formValue = this.aboutForm.getRawValue();
    const updatedVendorInfo = {
      ...this.about,
      ...formValue,
      msaexpiryDate: formatSingleDate(
        formValue.msaexpiryDate,
        yearDateFormat.date,
      ),
      dateOfOnboarding: formatSingleDate(
        formValue.dateOfOnboarding,
        yearDateFormat.date,
      ),
    };
    this.dataService.updateVendorDetails(updatedVendorInfo).subscribe({
      next: (res) => {
        this.about = updatedVendorInfo;
        this.toasterService.showSuccess(
          VendorProfile.vendorProfileUpdationSuccess,
          ToastTitle.SUCCESS,
        );
        this.aboutForm.reset();
        this.opened = false;
      },
      error: (error) => {
        this.toasterService.showError(
          VendorProfile.vendorProfileUpdationError,
          ToastTitle.ERROR,
        );
      },
    });
  }

  openContactDialog(index: number) {
    this.selectedContactIndex = index;
    const c = this.vendorContact[index] || {};
    this.selectedContactId = c.vendorDetailsId || '';
    this.contactForm.patchValue({
      pointOfContact: c.pointOfContact || '',
      designation: c.designation || '',
      email: c.email || '',
      contact: c.contact || '',
      location: c.location || '',
    });
  }

  saveContactDialog() {
    if (this.contactForm.invalid || this.selectedContactIndex === null) {
      this.contactForm.markAllAsTouched();
      return;
    }
    const updatedContactInfo = {
      ...this.vendorContact[this.selectedContactIndex],
      ...this.contactForm.getRawValue(),
      vendorId: this.vendorId,
      vendorDetailsId: this.selectedContactId,
    };
    const contactIndex = this.selectedContactIndex;
    this.dataService.updateVendorPocDetails(updatedContactInfo).subscribe({
      next: (res) => {
        this.vendorContact[contactIndex] = updatedContactInfo;
        this.toasterService.showSuccess(
          VendorProfile.vendorProfileUpdationSuccess,
          ToastTitle.SUCCESS,
        );
        this.contactForm.reset();
        this.openedCi = false;
      },
      error: (error) => {
        this.toasterService.showError(
          VendorProfile.vendorProfileUpdationError,
          ToastTitle.ERROR,
        );
      },
    });
  }

  trackByKey(_i: number, d: { key: string }) {
    return d.key;
  }
  trackContact(i: number) {
    return i;
  }

  getFieldType(key: string) {
    if (
      key.includes(vendorProfileInfo.keyMsaexpiryDate) ||
      key.includes(vendorProfileInfo.keyDateOfOnboarding)
    ) {
      return 'date';
    }
    if (key.includes(vendorProfileInfo.keyMobileNumber)) {
      return 'number';
    }
    if (key.includes('email')) {
      return 'email';
    }
    return 'text';
  }

  isFieldValid(formGroup: FormGroup, fieldName: string): boolean {
    const control = formGroup.get(fieldName);
    if (this.isSubmitted || control?.touched) {
      return control?.valid ?? false;
    }
    return true;
  }

  getFieldError(formGroup: FormGroup, fieldName: string): string {
    const control = formGroup.get(fieldName);
    return getFormControlError(control);
  }

  preventInvalidKeyPress(event: KeyboardEvent) {
    restrictInvalidKeyPress(event);
  }
}
